import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import Sticky from 'react-stickynode';
import isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import moment from 'moment';
import 'moment-countdown';

import InfoCircleFilled from '@ant-design/icons/InfoCircleFilled';

import Collapse from 'components/UI/Antd/Collapse/Collapse';

import useWindowSize from 'library/hooks/useWindowSize';
import {
  AlertWrapper,
  AlertInfo,
  TimerWrapper,
  Timer,
  TimerDescription,
  TimeLeft,
  CollapseWrapper,
  CollapseHeader
} from './NotificationAlert.style';

import timerIcon from 'assets/images/payments/timer-icon.svg';

const { Panel } = Collapse;

const NotificationAlert = ({
  reservation,
  action_required,
  status_code,
  dateLeft,
  expired
}) => {
  let timer = null;

  const { width } = useWindowSize();
  const { t } = useTranslation();

  const { payment, payment_accepted_online } = reservation;

  const [timeLeft, setTimeLeft] = useState(null);
  const [isHeaderHidden, setHeaderHidden] = useState(false);

  useScrollPosition(({ currPos }) => {
    if (width < 768) {
      if (currPos.y < -200) {
        if (!isHeaderHidden) setHeaderHidden(true);
      } else {
        if (isHeaderHidden) setHeaderHidden(false);
      }
    }
  });

  // ---------------------- //
  // TIMER
  // ---------------------- //

  const getTimeLeft = timerStartsAtMoment => {
    const countdown = timerStartsAtMoment.countdown();
    const { minutes, hours, seconds } = countdown;
    const hoursFormatted = hours.toString().length === 1 ? `0${hours}` : hours;
    const minutesFormatted =
      minutes.toString().length === 1 ? `0${minutes}` : minutes;
    const secondsFormatted =
      seconds.toString().length === 1 ? `0${seconds}` : seconds;
    const result = `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`;
    return result;
  };

  useEffect(() => {
    if (dateLeft) {
      const timerStartsAtMoment = moment(dateLeft);
      timer = setTimeout(() => {
        setTimeLeft(getTimeLeft(timerStartsAtMoment));
      }, 1000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  });

  useEffect(() => {
    if (dateLeft) {
      const timeExpired = dateLeft.isBefore(moment());
      if (timeExpired) {
        clearTimeout(timer);
      }
    }
  }, [timeLeft]);

  if (
    status_code === 'APPROVED' ||
    status_code === 'CANCELED_BY_BOATER' ||
    status_code === 'CANCELED_BY_MARINA' ||
    status_code === 'EXPIRED_NO_MARINA_RESPONSE' ||
    status_code === 'EXPIRED_NO_BOATER_RESPONSE'  ||
    status_code === 'WAITING_LIST' ||
    status_code === 'MARINA_ADDED_DATES'
  )
    return null;

  const type =
    (status_code === 'UPDATED' &&
      action_required === 'BOATER' &&
      !isEmpty(payment)) ||
    (status_code === 'PAYMENT_EXPIRED' && action_required === 'BOATER')
      ? 'timer'
      : 'notification';

  const getAlertText = () => {
    let text = '';
    if ((status_code === 'INITIATED' || status_code === 'BOATER_ACCEPTED_SUGGESTED_DATES') && action_required === 'MARINA') {
      text = t('REQUEST_SENT_WAITING_THE_MARINA_FOR_PRICE_AND_REQUIREMENTS');
    }
    return text;
  };

  const renderTimeLeft = () => {
    return (
      <TimeLeft>
        <span className='timer-title'>
          {expired
            ? t('RESERVATION_PAYMENT_COUNTDOWN_ENDED_BANNER_TITLE')
            : t('RESERVATION_PAYMENT_COUNTDOWN_BANNER_TITLE')}
        </span>
        { !expired && <span className='timer-clock'> {timeLeft}</span> }
      </TimeLeft>
    );
  };

  const renderAlert = () => {
    if (type === 'notification') {
      return (
        <AlertWrapper>
          <AlertInfo>
            <InfoCircleFilled style={{ color: '#194AB9' }} />
            <p>{getAlertText()}</p>
          </AlertInfo>
        </AlertWrapper>
      );
    } else {
      if (width > 767) {
        return (
          <TimerWrapper
            className={clsx({
              'with-payment': payment_accepted_online,
              expired: expired,
              'no-payment': !reservation.payment_accepted_online
            })}
          >
            <Timer>
              {!expired && <img src={timerIcon} />}
              <TimerDescription>
                {renderTimeLeft()}
                <span className='timer-subtitle'>
                  {expired
                    ? t('RESERVATION_PAYMENT_COUNTDOWN_ENDED_BANNER_DISC')
                    : t('RESERVATION_PAYMENT_COUNTDOWN_BANNER_DISC')}
                </span>
              </TimerDescription>
            </Timer>
          </TimerWrapper>
        );
      } else {
        return (
          <CollapseWrapper>
            <Collapse
              expandIconPosition='right'
              bordered={false}
              ghost={true}
              className={expired ? 'expired' : ''}
            >
              <Panel
                key='1'
                className='alert-collapse-panel'
                header={
                  <CollapseHeader className={expired ? 'expired' : ''}>
                    {!expired && <img src={timerIcon} />}
                    {renderTimeLeft()}
                  </CollapseHeader>
                }
              >
                <TimerDescription>
                  <span className='timer-subtitle'>
                    {expired
                      ? t('RESERVATION_PAYMENT_COUNTDOWN_ENDED_BANNER_DISC')
                      : t('RESERVATION_PAYMENT_COUNTDOWN_BANNER_DISC')}
                  </span>
                </TimerDescription>
              </Panel>
            </Collapse>
          </CollapseWrapper>
        );
      }
    }
  };

  if (width > 767) {
    return renderAlert();
  } else {
    return (
      <Sticky
        top={isHeaderHidden ? 0 : 52}
        innerZ={10000}
        activeClass='isNotificationSticky'
        className='alert-sticky-wrapper'
      >
        {renderAlert()}
      </Sticky>
    );
  }
};

export default NotificationAlert;
